
import { useEffect, useState } from 'react';
import { useFastingPersistence } from './persistence';
import { useTimerLogic } from './useTimerLogic';
import { useFastingActions } from './useFastingActions';

export const useFastingState = () => {
  // Use the persistence hook to manage state
  const persistence = useFastingPersistence();
  
  // Don't initialize timer logic until persistence is loaded
  // Initialize timer logic with persisted values
  const timerLogic = useTimerLogic({
    initialIsActive: persistence.isLoaded ? persistence.isActive : false,
    initialIsPaused: persistence.isLoaded ? persistence.isPaused : false,
    initialTimeElapsed: persistence.isLoaded ? persistence.timeElapsed : 0,
    initialStartTime: persistence.isLoaded ? persistence.startTime : null,
    initialPausedTime: persistence.isLoaded ? persistence.pausedTime : null,
    initialTotalPauseDuration: persistence.isLoaded ? persistence.totalPauseDuration : 0,
    selectedProtocol: persistence.selectedProtocol,
    isLoaded: persistence.isLoaded
  });
  
  // Sync timer state changes back to persistence
  // Only sync when there are meaningful changes to avoid conflicts
  useEffect(() => {
    if (persistence.isActive !== timerLogic.isActive) {
      persistence.setIsActive(timerLogic.isActive);
    }
  }, [timerLogic.isActive]);
  
  useEffect(() => {
    if (persistence.isPaused !== timerLogic.isPaused) {
      persistence.setIsPaused(timerLogic.isPaused);
    }
  }, [timerLogic.isPaused]);
  
  // Throttle timeElapsed sync to avoid conflicts with timer interval
  useEffect(() => {
    const syncTimeout = setTimeout(() => {
      if (Math.abs(persistence.timeElapsed - timerLogic.timeElapsed) > 5) {
        persistence.setTimeElapsed(timerLogic.timeElapsed);
      }
    }, 2000); // Wait 2 seconds before syncing timeElapsed
    
    return () => clearTimeout(syncTimeout);
  }, [timerLogic.timeElapsed]);
  
  useEffect(() => {
    if (persistence.startTime !== timerLogic.startTime) {
      persistence.setStartTime(timerLogic.startTime);
    }
  }, [timerLogic.startTime]);
  
  useEffect(() => {
    if (persistence.pausedTime !== timerLogic.pausedTime) {
      persistence.setPausedTime(timerLogic.pausedTime);
    }
  }, [timerLogic.pausedTime]);
  
  useEffect(() => {
    if (persistence.totalPauseDuration !== timerLogic.totalPauseDuration) {
      persistence.setTotalPauseDuration(timerLogic.totalPauseDuration);
    }
  }, [timerLogic.totalPauseDuration]);
  
  // Use the actions hook for fasting actions
  const actions = useFastingActions({
    setIsActive: timerLogic.setIsActive,
    setIsPaused: timerLogic.setIsPaused,
    setTimeElapsed: timerLogic.setTimeElapsed,
    setStartTime: timerLogic.setStartTime,
    setPausedTime: timerLogic.setPausedTime,
    setEndTime: persistence.setEndTime,
    setWaterIntake: persistence.setWaterIntake,
    setSelectedProtocol: persistence.setSelectedProtocol,
    setTotalPauseDuration: timerLogic.setTotalPauseDuration,
    isActive: timerLogic.isActive,
    isPaused: timerLogic.isPaused,
    startTime: timerLogic.startTime,
    pausedTime: timerLogic.pausedTime,
    endTime: persistence.endTime,
    timeElapsed: timerLogic.timeElapsed,
    totalPauseDuration: timerLogic.totalPauseDuration,
    selectedProtocol: persistence.selectedProtocol,
    fastingHistory: timerLogic.fastingHistory
  });

  return {
    isActive: timerLogic.isActive,
    isPaused: timerLogic.isPaused,
    timeElapsed: timerLogic.timeElapsed,
    selectedProtocol: persistence.selectedProtocol,
    waterIntake: persistence.waterIntake,
    endTime: persistence.endTime,
    totalPauseDuration: timerLogic.totalPauseDuration,
    estimatedCaloriesBurned: timerLogic.estimatedCaloriesBurned,
    actions
  };
};
